import { useSelector } from 'react-redux';
import { WebDocName, WEB_DOCS, IDENTITY_WEB_DOC_TYES } from '~/constants/docs.ts';
import useGetContragentTypes from '../../../hooks/useGetContragentTypes.ts';
import { useEffect, useMemo, useState } from 'react';
import { OwnerType } from '../../OwnershipStructure/types.ts';
import { TDocument } from '../types.ts';
import useGetUserRole from '~/hooks/useGetUserRole.ts';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext.tsx';
import { checkDateAdditionalVerificationApplication } from '~/modules/identification/forms/Documents/utils.ts';
import {
  selectAccountHolderStatusSelectedOption,
  selectAdditionalInformationFatca,
  selectAdditionalInformationIsForeignFinancialUnderFATCA,
  selectControllingPersons,
  selectDocuments,
  selectFatcaAdditionalInformation,
  selectGeneralInfo,
  selectGeneralInfoLegalResident,
  selectHasForeignTaxResidency,
  selectIdentification,
  selectIsAccountHolder,
  selectIsControllingPersonTaxResidentOtherJurisdiction,
  selectIsOtherTaxResident,
  selectManagersAndRepresentatives,
  selectManagmentStructure,
  selectOwnershipStructure,
  selectPurposeOfBusinessRelations,
} from '~/store/identification-form/selectors.ts';
import { useBeneficiaryOwners } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/hooks/useBeneficiaryOwners.ts';
import { showBeneficiaryOwners } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/constants.ts';
import { TControllingPersons } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/types.ts';
import { REFUTATION_DOC_TYPE } from '~/modules/identification/forms/AdditionalInformation/constants.ts';
import { useUSACountryFlags } from '~/modules/identification/forms/GeneralInfo/hooks/useUSACountryFlags.ts';
import { FatcaStatusNFFE } from '~/modules/identification/forms/FatcaAdditionalInformation/constants/constants.ts';

export const useGetDocumentIdsSections = () => {
  const { isAssetManager } = useGetUserRole();
  const {
    isCRSIFopEnabled,
    isOwnershipStructureTreeEnabled,
    isAdditionalInformationAboutTaxResidencyEnabled,
    isFATCALegalResidentEnabled,
    isFATCAFopEnabled,
  } = useFeatureFlags();

  const {
    isNewOrNonProfitEntity,
    isPremisesInConflictZone,
    isNonProfitOrganization,
    isLicensed,
    hasBusinessActivityChanges,
  } = useSelector(selectPurposeOfBusinessRelations) || {};
  const { documents, registrationDate } = useSelector(selectDocuments) || {};
  const { isUnifiedAccountAccess, isAnyAssetManagerDataChanged } =
    useSelector(selectManagersAndRepresentatives) || {};
  const { isBeneficiariesAbsent, isFoundersAndBeneficiariesAbsent, owners } =
    useSelector(selectOwnershipStructure) || {};
  const { assetManagers } = useSelector(selectManagersAndRepresentatives) || {};
  const { beneficiaryOwners } = useBeneficiaryOwners();
  const generalInfo = useSelector(selectGeneralInfo) || {};
  const { isUSTaxResident, isForeignFinancialUnderFATCA, isControllingPersonUSTaxResident } =
    useSelector(selectGeneralInfoLegalResident) || {};
  const isAccountHolder = useSelector(selectIsAccountHolder);
  const isOtherTaxResident = useSelector(selectIsOtherTaxResident);
  const isControllingPersonTaxResidentOtherJurisdiction = useSelector(
    selectIsControllingPersonTaxResidentOtherJurisdiction
  );
  const accountHolderStatusSelectedOption = useSelector(selectAccountHolderStatusSelectedOption);
  const controllingPersons = useSelector(selectControllingPersons);
  const isForeignFinancialUnderFATCAFop = useSelector(
    selectAdditionalInformationIsForeignFinancialUnderFATCA
  );
  const { refutationDocType } = useSelector(selectAdditionalInformationFatca);
  const fatcaAdditionalInformation = useSelector(selectFatcaAdditionalInformation) || {};
  const nffeInfo = fatcaAdditionalInformation?.nffeInfo;

  const { birthplaceCountryUSA, permanentAddressUSA, citizenshipCountryUSA } = useUSACountryFlags();

  const showOtherAssetManagerDoc =
    !isAccountHolder || (isAccountHolder && (assetManagers ?? [])?.length > 1);
  const isTaxResidentOtherJurisdictionControllingPersons = useMemo(() => {
    if (!controllingPersons) return [];

    return controllingPersons.filter(
      (controllingPerson: TControllingPersons) => controllingPerson.isTaxResidentOtherJurisdiction
    );
  }, [controllingPersons]);

  const crsRelevantOwners = useMemo(() => {
    const isCrsRelevant =
      (isOtherTaxResident || isControllingPersonTaxResidentOtherJurisdiction) &&
      showBeneficiaryOwners.includes(accountHolderStatusSelectedOption!);

    return isCrsRelevant ? isTaxResidentOtherJurisdictionControllingPersons : [];
  }, [
    isOtherTaxResident,
    isControllingPersonTaxResidentOtherJurisdiction,
    accountHolderStatusSelectedOption,
    isTaxResidentOtherJurisdictionControllingPersons,
  ]);

  const isDisabledDiagramGeneration =
    owners?.length > 0 && owners.every((owner) => owner.type === OwnerType.INDIVIDUAL);

  const { isReidentification } = useSelector(selectIdentification);

  const { isHeadNew } = useSelector(selectManagmentStructure) || {};

  const hasForeignTaxResidency = useSelector(selectHasForeignTaxResidency);

  const {
    additionalMainDocuments = [],
    assetManagersDocuments = [],
    additionalDocuments = [],
    mainDocuments = [],
  } = documents;

  const license = 'license' in generalInfo ? generalInfo.license : undefined;

  const isAccountManagementAuthorizedPersons = mainDocuments.some(
    (doc) => doc.idDictWebDocument === WEB_DOCS[WebDocName.accountManagementAuthorizedPersons]
  );

  const { isSoleTrader, isLegalResident } = useGetContragentTypes();

  const hasAdditionalVerificationApplication =
    documents?.mainDocuments?.some(
      (doc) =>
        doc.idDictWebDocument === WEB_DOCS[WebDocName.additionalVerificationApplication] &&
        doc.idDoc !== null
    ) ?? false;
  const checkDateAdditionalVerificationApp = checkDateAdditionalVerificationApplication();

  const showFATCALegalResident =
    isFATCALegalResidentEnabled &&
    (isForeignFinancialUnderFATCA || isControllingPersonUSTaxResident || isUSTaxResident);

  const showFATCAFop =
    (birthplaceCountryUSA || permanentAddressUSA) &&
    !citizenshipCountryUSA &&
    isForeignFinancialUnderFATCAFop === false;

  const passiveNffeWithSubstantialUsOwners =
    isControllingPersonUSTaxResident &&
    nffeInfo?.fatcaStatusNFFE === FatcaStatusNFFE.PASSIVE_NFFE_WITH_SUBSTANTIAL_US_OWNERS;

  const isNativeCountryUSA = permanentAddressUSA || birthplaceCountryUSA;

  const showFatcaExplanation =
    isFATCAFopEnabled &&
    isForeignFinancialUnderFATCAFop === false &&
    isNativeCountryUSA &&
    refutationDocType === REFUTATION_DOC_TYPE.EXPLANATION;

  const getBlock1Ids = () => {
    const blockIds = [];
    const showAuthPersons =
      isAccountManagementAuthorizedPersons &&
      isUnifiedAccountAccess &&
      isAnyAssetManagerDataChanged;
    const showOwnershipStructureSchema =
      isOwnershipStructureTreeEnabled &&
      !isFoundersAndBeneficiariesAbsent &&
      !isDisabledDiagramGeneration;

    if (isLegalResident) {
      if (isReidentification) {
        blockIds.push(
          ...[
            WEB_DOCS[WebDocName.edrExcerpt],
            WEB_DOCS[WebDocName.clientQuestionnaire],
            WEB_DOCS[WebDocName.informationDisclosureConsent],
          ]
        );
        if (isHeadNew) {
          blockIds.push(WEB_DOCS[WebDocName.personalDataInformationAgreement]);
        }
        if (isNewOrNonProfitEntity) {
          blockIds.push(WEB_DOCS[WebDocName.explanatoryLetter]);
        }
        if (showAuthPersons) {
          blockIds.push(WEB_DOCS[WebDocName.accountManagementAuthorizedPersons]);
        }
        if (
          (isOtherTaxResident || isControllingPersonTaxResidentOtherJurisdiction) &&
          isAdditionalInformationAboutTaxResidencyEnabled
        ) {
          blockIds.push(WEB_DOCS[WebDocName.crsEntityTaxResidencyAssessment]);
        }
        if (isAdditionalInformationAboutTaxResidencyEnabled && crsRelevantOwners.length) {
          crsRelevantOwners.forEach(() => {
            blockIds.push(WEB_DOCS[WebDocName.crsControllingPersonTaxResidencyAssessment]);
          });
        }
        if (showFATCALegalResident) {
          blockIds.push(WEB_DOCS[WebDocName.fatcaLegalEntityAppendix3]);
        }
      } else {
        blockIds.push(WEB_DOCS[WebDocName.clientQuestionnaire]);
        if (isNewOrNonProfitEntity) {
          blockIds.push(WEB_DOCS[WebDocName.explanatoryLetter]);
        }
        if (isPremisesInConflictZone) {
          blockIds.push(WEB_DOCS[WebDocName.coverLetter]);
        }
        if (hasAdditionalVerificationApplication || !checkDateAdditionalVerificationApp) {
          blockIds.push(WEB_DOCS[WebDocName.additionalVerificationApplication]);
        }
        if (
          (isOtherTaxResident || isControllingPersonTaxResidentOtherJurisdiction) &&
          isAdditionalInformationAboutTaxResidencyEnabled
        ) {
          blockIds.push(WEB_DOCS[WebDocName.crsEntityTaxResidencyAssessment]);
        }
        if (isAdditionalInformationAboutTaxResidencyEnabled && crsRelevantOwners.length) {
          crsRelevantOwners.forEach(() => {
            blockIds.push(WEB_DOCS[WebDocName.crsControllingPersonTaxResidencyAssessment]);
          });
        }
        if (showFATCALegalResident) {
          blockIds.push(WEB_DOCS[WebDocName.fatcaLegalEntityAppendix3]);
        }
        blockIds.push(
          ...[
            WEB_DOCS[WebDocName.currentAccountOpeningApplication],
            WEB_DOCS[WebDocName.remoteServiceConnectionApplication],
            WEB_DOCS[WebDocName.publicOfferAgreementApplication],
            WEB_DOCS[WebDocName.edrExcerpt],
            WEB_DOCS[WebDocName.accountManagementAuthorizedPersons],
            WEB_DOCS[WebDocName.informationDisclosureConsent],
            WEB_DOCS[WebDocName.personalDataInformationAgreement],
          ]
        );
      }
      if (showOwnershipStructureSchema) {
        blockIds.push(WEB_DOCS[WebDocName.ownershipStructureScheme]);
      }
    }

    if (isSoleTrader) {
      if (isReidentification) {
        blockIds.push(
          ...[
            WEB_DOCS[WebDocName.clientQuestionnaire],
            WEB_DOCS[WebDocName.edrExcerpt],
            WEB_DOCS[WebDocName.informationDisclosureConsent],
          ]
        );
        if (hasBusinessActivityChanges) {
          blockIds.push(WEB_DOCS[WebDocName.explanatoryLetter]);
        }
        if (isCRSIFopEnabled && hasForeignTaxResidency) {
          blockIds.push(WEB_DOCS[WebDocName.crsIndividualTaxResidencyAssessment]);
        }
        if (showFatcaExplanation) {
          blockIds.push(WEB_DOCS[WebDocName.fatcaExplanation]);
        }
      } else {
        blockIds.push(
          ...[
            WEB_DOCS[WebDocName.clientQuestionnaire],
            WEB_DOCS[WebDocName.currentAccountOpeningApplication],
            WEB_DOCS[WebDocName.remoteServiceConnectionApplication],
            WEB_DOCS[WebDocName.publicOfferAgreementApplication],
            WEB_DOCS[WebDocName.edrExcerpt],
            WEB_DOCS[WebDocName.informationDepositGuarantee],
            WEB_DOCS[WebDocName.informationDisclosureConsent],
            WEB_DOCS[WebDocName.explanatoryLetter],
            WEB_DOCS[WebDocName.personalDataInformationAgreement],
          ]
        );
        if (hasAdditionalVerificationApplication || !checkDateAdditionalVerificationApp) {
          blockIds.push(WEB_DOCS[WebDocName.additionalVerificationApplication]);
        }
        if (isCRSIFopEnabled && hasForeignTaxResidency) {
          blockIds.push(WEB_DOCS[WebDocName.crsIndividualTaxResidencyAssessment]);
        }
        if (showFatcaExplanation) {
          blockIds.push(WEB_DOCS[WebDocName.fatcaExplanation]);
        }
      }
    }

    if (isAssetManager) {
      blockIds.push(WEB_DOCS[WebDocName.personalDataInformationAgreement]);
    }

    return blockIds;
  };

  const getBlock2Ids = () => {
    const blockIds: number[] = [];

    if (isLegalResident) {
      if (isReidentification) {
        const mandatoryDocs = [
          WEB_DOCS[WebDocName.taxpayerIdentificationNumber],
          WEB_DOCS[WebDocName.latestConstituentDocuments],
          WEB_DOCS[WebDocName.foundersMinutesApproveConstitutionalDocs],
          WEB_DOCS[WebDocName.foundersMinutesAppointDirector],
          WEB_DOCS[WebDocName.orderAppointDirector],
          ...(isLicensed ? [WEB_DOCS[WebDocName.activityLicensePermit]] : []),
          ...(showOtherAssetManagerDoc
            ? [WEB_DOCS[WebDocName.orderAppointOtherAuthorizedPersons]]
            : []),
          ...(!isBeneficiariesAbsent
            ? beneficiaryOwners.flatMap(() => [
                WEB_DOCS[WebDocName.uboPassport],
                WEB_DOCS[WebDocName.uboTinCode],
              ])
            : []),
          ...(!isUnifiedAccountAccess && isAnyAssetManagerDataChanged
            ? [WEB_DOCS[WebDocName.accountManagementAuthorizedPersons]]
            : []),
          ...(isFATCALegalResidentEnabled && (passiveNffeWithSubstantialUsOwners || isUSTaxResident)
            ? [WEB_DOCS[WebDocName.fatcaFormW9]]
            : []),
          ...(isFATCALegalResidentEnabled && passiveNffeWithSubstantialUsOwners
            ? [WEB_DOCS[WebDocName.fatcaUsTinCopy]]
            : []),
          ...(isFATCALegalResidentEnabled && isUSTaxResident
            ? [WEB_DOCS[WebDocName.fatcaUsTinEinCopy]]
            : []),
          ...(showFATCALegalResident ? [WEB_DOCS[WebDocName.fatcaFormW8benE]] : []),
        ];

        blockIds.push(...mandatoryDocs);
      } else {
        const mandatoryDocs = [
          WEB_DOCS[WebDocName.taxpayerIdentificationNumber],
          WEB_DOCS[WebDocName.latestConstituentDocuments],
          WEB_DOCS[WebDocName.foundersMinutesApproveConstitutionalDocs],
          WEB_DOCS[WebDocName.foundersMinutesAppointDirector],
          WEB_DOCS[WebDocName.orderAppointDirector],
          ...(isLicensed ? [WEB_DOCS[WebDocName.activityLicensePermit]] : []),
          ...(showOtherAssetManagerDoc
            ? [WEB_DOCS[WebDocName.orderAppointOtherAuthorizedPersons]]
            : []),
          ...(!isBeneficiariesAbsent
            ? beneficiaryOwners.flatMap(() => [
                WEB_DOCS[WebDocName.uboPassport],
                WEB_DOCS[WebDocName.uboTinCode],
              ])
            : []),
          ...(isNonProfitOrganization
            ? [WEB_DOCS[WebDocName.decisionIncludeNonprofitRegister]]
            : []),
          ...(isFATCALegalResidentEnabled && (passiveNffeWithSubstantialUsOwners || isUSTaxResident)
            ? [WEB_DOCS[WebDocName.fatcaFormW9]]
            : []),
          ...(isFATCALegalResidentEnabled && passiveNffeWithSubstantialUsOwners
            ? [WEB_DOCS[WebDocName.fatcaUsTinCopy]]
            : []),
          ...(isFATCALegalResidentEnabled && isUSTaxResident
            ? [WEB_DOCS[WebDocName.fatcaUsTinEinCopy]]
            : []),
          ...(showFATCALegalResident ? [WEB_DOCS[WebDocName.fatcaFormW8benE]] : []),
        ];

        blockIds.push(...mandatoryDocs);
      }
    } else {
      if (license === true) blockIds.push(WEB_DOCS[WebDocName.activityLicensePermit]);
      !blockIds.includes(WEB_DOCS[WebDocName.taxpayerIdentificationNumber]) &&
        blockIds.push(WEB_DOCS[WebDocName.taxpayerIdentificationNumber]);

      if (isFATCAFopEnabled) {
        if (isForeignFinancialUnderFATCAFop) {
          const mandatoryDocs = [
            WEB_DOCS[WebDocName.fatcaFormW9],
            WEB_DOCS[WebDocName.fatcaUsTinCopy],
          ];

          blockIds.push(...mandatoryDocs);
        } else {
          if (showFATCAFop) {
            blockIds.push(WEB_DOCS[WebDocName.fatcaFormW8ben]);
          }
          if (refutationDocType === REFUTATION_DOC_TYPE.DS_4083) {
            blockIds.push(WEB_DOCS[WebDocName.fatcaFormDs4083]);
          }
          if (refutationDocType === REFUTATION_DOC_TYPE.I_407) {
            blockIds.push(WEB_DOCS[WebDocName.fatcaFormI407]);
          }
        }
      }
    }
    if (registrationDate) {
      const currentDate = new Date();
      const [day, month, year] = registrationDate.split('.');
      const registrationDateObj = new Date(`${year}-${month}-${day}`);
      const diffInTime = currentDate.getTime() - registrationDateObj.getTime();
      const diffInDays = diffInTime / (1000 * 3600 * 24);

      if (isSoleTrader) {
        if (diffInDays > 365) blockIds.push(WEB_DOCS[WebDocName.taxDeclaration]);
      } else if (isLegalResident) {
        if (diffInDays > 365)
          blockIds.push(WEB_DOCS[WebDocName.financialStatementsWithStatisticsMark]);
      }
    }

    additionalMainDocuments.map((document: TDocument) => {
      if (!blockIds.includes(document.idDictWebDocument)) {
        blockIds.push(document.idDictWebDocument);
      }
    });

    if (!blockIds.some((item) => IDENTITY_WEB_DOC_TYES.includes(item))) {
      blockIds.push(WEB_DOCS[WebDocName.passport]);
    }

    return blockIds;
  };

  const getBlock3Ids = () => {
    const blockIds: number[] = [];

    const uniqueDocuments = new Set<number>();

    blockIds.push(
      ...(isAssetManager
        ? [
            WEB_DOCS[WebDocName.residenceConfirmationDocument],
            WEB_DOCS[WebDocName.informationInternallyDisplacedPerson],
            WEB_DOCS[WebDocName.other],
          ]
        : []),
      ...(isSoleTrader
        ? [
            WEB_DOCS[WebDocName.bankStatementFromAnotherBank],
            WEB_DOCS[WebDocName.contractWithCounterparty],
            WEB_DOCS[WebDocName.leaseAgreement],
            WEB_DOCS[WebDocName.contractRelatedDocuments],
            WEB_DOCS[WebDocName.fundsOriginSources],
            WEB_DOCS[WebDocName.informationLetter],
            WEB_DOCS[WebDocName.vatStatusConfirmation],
            WEB_DOCS[WebDocName.unifiedContributionRegistrationDocument],
            WEB_DOCS[WebDocName.residenceConfirmationDocument],
            WEB_DOCS[WebDocName.informationInternallyDisplacedPerson],
            WEB_DOCS[WebDocName.other],
          ]
        : []),
      ...(isLegalResident
        ? [
            WEB_DOCS[WebDocName.bankStatementFromAnotherBank],
            WEB_DOCS[WebDocName.contractWithCounterparty],
            WEB_DOCS[WebDocName.leaseAgreement],
            WEB_DOCS[WebDocName.trialBalance],
            WEB_DOCS[WebDocName.contractRelatedDocuments],
            WEB_DOCS[WebDocName.fundsOriginSources],
            WEB_DOCS[WebDocName.informationLetter],
            WEB_DOCS[WebDocName.staffingSchedule4df],
            WEB_DOCS[WebDocName.financialAssistanceLoanAgreement],
            WEB_DOCS[WebDocName.activityAdditionalDocuments],
            WEB_DOCS[WebDocName.foundersProtocolDecisionChange],
            WEB_DOCS[WebDocName.powerOfAttorney],
            WEB_DOCS[WebDocName.residenceConfirmationDocument],
            WEB_DOCS[WebDocName.orderGrantSigningRights],
            WEB_DOCS[WebDocName.employmentContractWithSigningRights],
            WEB_DOCS[WebDocName.sharePurchaseAgreement],
            WEB_DOCS[WebDocName.financialInstitutionsRegisterProof],
            WEB_DOCS[WebDocName.shareholdersRegisterExtract],
            ...(isOwnershipStructureTreeEnabled && !isReidentification
              ? [WEB_DOCS[WebDocName.ownershipStructureInfo]]
              : []),
            WEB_DOCS[WebDocName.constituentDocumentAccessCodeDescription],
            WEB_DOCS[WebDocName.informationInternallyDisplacedPerson],
            WEB_DOCS[WebDocName.other],
          ]
        : [])
    );

    additionalDocuments.forEach(({ idDictWebDocument }: TDocument) => {
      const isAlreadyInBlock = blockIds.includes(idDictWebDocument);
      const isDuplicateForbidden = uniqueDocuments.has(idDictWebDocument);

      if (!isAlreadyInBlock || !isDuplicateForbidden) {
        blockIds.push(idDictWebDocument);
      }
    });

    return blockIds;
  };

  const getBlock4Ids = () => {
    if (isLegalResident) {
      return assetManagersDocuments.map((document: TDocument) => document.idDictWebDocument);
    } else return [];
  };

  const [mainConditionallyRequiredIds, setMainConditionallyRequiredIds] = useState<number[]>([]);
  const [additionalConditionallyRequiredIds, setAdditionalConditionallyRequiredIds] = useState<
    number[]
  >([]);
  const [additionalAlwaysOptionalIds, setAdditionalAlwaysOptionalIds] = useState<number[]>([]);
  const [assetManagersDocumentsIds, setAssetManagersDocumentsIds] = useState<number[]>([]);

  useEffect(() => {
    setMainConditionallyRequiredIds(getBlock1Ids());
  }, [
    isLegalResident,
    isNewOrNonProfitEntity,
    isPremisesInConflictZone,
    isSoleTrader,
    isReidentification,
    crsRelevantOwners,
    isAdditionalInformationAboutTaxResidencyEnabled,
  ]);

  useEffect(() => {
    setAdditionalConditionallyRequiredIds(getBlock2Ids());
  }, [
    isLegalResident,
    license,
    isLicensed,
    showOtherAssetManagerDoc,
    isBeneficiariesAbsent,
    registrationDate,
    isSoleTrader,
    additionalMainDocuments,
    beneficiaryOwners,
  ]);

  useEffect(() => {
    setAdditionalAlwaysOptionalIds(getBlock3Ids());
  }, [isLegalResident, isAssetManager, isSoleTrader, additionalDocuments]);

  useEffect(() => {
    setAssetManagersDocumentsIds(getBlock4Ids());
  }, [isLegalResident, assetManagersDocuments]);

  return {
    mainConditionallyRequiredIds,
    additionalConditionallyRequiredIds,
    additionalAlwaysOptionalIds,
    assetManagersDocumentsIds,
    setMainConditionallyRequiredIds,
    setAdditionalConditionallyRequiredIds,
    setAdditionalAlwaysOptionalIds,
    setAssetManagersDocumentsIds,
  };
};
